import { TWidget } from 'widgets/Widget';
import { isMediumViewAndDown } from 'widgets/toolbox/viewtype';

function ImageWithCtaShowcaseClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory pagedesigner
     * @class ImageWithCtaShowcase
     * @augments Widget
     * @classdesc Represents ImageWithCtaShowcase component with features:
     * 1. Sync active mobile pagination dot with secondary track horizontal scroll position
     * 2. Scroll to selected secondary slide when pagination dot is clicked
     *
     * <div id="${pdict.ID}" class="b-image-with-cta-showcase ${pdict.mods}" data-widget="imageWithCtaShowcase">
     *     <div class="b-image-with-cta-showcase-featured">
     *         <!-- featured imageWithCta component -->
     *     </div>
     *     <div class="b-image-with-cta-showcase-secondary" data-ref="secondary">
     *         <div class="b-image-with-cta-showcase-secondary-track" data-ref="track">
     *             <!-- secondary imageWithCta components -->
     *         </div>
     *         <div class="b-image-with-cta-showcase-pagination" role="tablist" data-ref="pagination">
     *             <button
     *                 type="button"
     *                 class="b-image-with-cta-showcase-pagination-dot m-active"
     *                 data-index="0"
     *                 data-event-click="onDotClick"
     *             ></button>
     *             <button
     *                 type="button"
     *                 class="b-image-with-cta-showcase-pagination-dot"
     *                 data-index="1"
     *                 data-event-click="onDotClick"
     *             ></button>
     *         </div>
     *     </div>
     * </div>
     */
    class ImageWithCtaShowcase extends Widget {
        track: HTMLElement | null = null;

        dots: Array<HTMLElement> = [];

        init() {
            super.init();

            this.track = this.ref('self').get()?.querySelector('.b-image-with-cta-showcase-secondary-track') || null;
            this.dots = Array.from(this.ref('self').get()?.querySelectorAll('.b-image-with-cta-showcase-pagination-dot') || []);

            if (!this.track || !this.dots.length) {
                return;
            }

            this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
            this.updateActiveDot();
        }

        private getMobileSlideWidth(): number {
            if (!this.track) {
                return 0;
            }

            const firstSlide = this.track.children[0] as HTMLElement | undefined;

            return firstSlide ? firstSlide.clientWidth : this.track.clientWidth;
        }

        private updateActiveDot() {
            if (!this.track || !this.dots.length) {
                return;
            }

            const slideWidth = this.getMobileSlideWidth();

            if (!slideWidth) {
                return;
            }

            const index = Math.round(this.track.scrollLeft / slideWidth);
            const safeIndex = Math.max(0, Math.min(index, this.dots.length - 1));

            this.dots.forEach((dot, dotIndex) => {
                dot.classList.toggle('m-active', dotIndex === safeIndex);
            });
        }

        private onTrackScroll = () => {
            if (!isMediumViewAndDown()) {
                return;
            }

            this.updateActiveDot();
        };

        onDotClick(source: unknown) {
            if (!this.track || !isMediumViewAndDown()) {
                return;
            }

            const sourceElement = source && typeof source === 'object' && 'get' in source
                ? (source as { get: () => HTMLElement | null }).get()
                : null;

            if (!sourceElement) {
                return;
            }

            const indexRaw = sourceElement.getAttribute('data-index');
            const index = indexRaw ? Number(indexRaw) : NaN;

            if (Number.isNaN(index)) {
                return;
            }

            const slideWidth = this.getMobileSlideWidth();
            const left = Math.max(0, index) * slideWidth;

            this.track.scrollTo({
                left: left,
                behavior: 'smooth'
            });
        }
    }

    return ImageWithCtaShowcase;
}

export type TImageWithCtaShowcase = ReturnType<typeof ImageWithCtaShowcaseClassCreator>;

export default ImageWithCtaShowcaseClassCreator;
